import { useState, useRef } from "react";
import {
  FileSpreadsheet,
  Download,
  ChevronLeft,
  ChevronRight,
  Check,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { utils, writeFileXLSX, read } from "xlsx";
import { BulkCreateOrderReviewForm } from "./bulk-create-order-review-form";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";
import { CreateOrderSchema } from "@/api.schemas";
import { logger } from "@/services/logger";
import { useCustomerAddresses } from "@/api/use-customer-addresses";

const TEMPLATE_HEADERS = [
  "Sender Name",
  "Sender Phone (Do not add +1)",
  "Sender Address",
  "Recipient Name",
  "Recipient Phone (Do not add +1)",
  "Recipient Address",
  "Pickup Notes",
  "Delivery Notes",
  "Parcels Count Number",
];

interface ParsedOrder {
  senderName: string;
  senderPhone: string;
  senderAddress: string;
  recipientName: string;
  recipientPhone: string;
  recipientAddress: string;
  pickupNotes?: string;
  deliveryNotes?: string;
  parcelsCount: number;
}

type ExcelRow = Record<(typeof TEMPLATE_HEADERS)[number], string>;

interface CreateOrderResponse {
  orderId: string;
}

export function BulkUploadForm() {
  const { token } = useSession();
  const { addresses } = useCustomerAddresses();
  const [files, setFiles] = useState<File[]>([]);
  const [isInvalid, setIsInvalid] = useState(false);
  const [parsedOrders, setParsedOrders] = useState<ParsedOrder[]>([]);
  const [isReviewing, setIsReviewing] = useState(false);
  const [currentOrderIndex, setCurrentOrderIndex] = useState(0);
  const currentFormSubmit = useRef<(() => void) | null>(null);
  const [completedOrders, setCompletedOrders] = useState<Set<number>>(
    new Set()
  );

  const primaryAddress = addresses?.find((addr) => addr.isPrimary);

  const createOrderMutation = useMutation({
    mutationFn: async (data: CreateOrderSchema) => {
      const response = await axios.post<CreateOrderResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/create`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
  });

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = droppedFiles.filter((file) =>
      /(csv|excel|spreadsheet|sheet|application\/vnd.ms-excel|application\/vnd.openxmlformats-officedocument.spreadsheetml.sheet)/.exec(
        file.type
      )
    );
    if (validFiles.length !== droppedFiles.length) {
      setIsInvalid(true);
    } else {
      setFiles(validFiles);
      setIsInvalid(false);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files ?? []);
    const validFiles = selectedFiles.filter((file) =>
      /(csv|excel|spreadsheet|sheet|application\/vnd.ms-excel|application\/vnd.openxmlformats-officedocument.spreadsheetml.sheet)/.exec(
        file.type
      )
    );
    if (validFiles.length !== selectedFiles.length) {
      setIsInvalid(true);
    } else {
      setFiles(validFiles);
      setIsInvalid(false);
    }
  };

  const parseExcelFile = async (file: File) => {
    const data = await file.arrayBuffer();
    const workbook = read(data);
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const jsonData = utils.sheet_to_json<Partial<ExcelRow>>(worksheet);

    const orders = jsonData
      .filter((row) => row["Sender Name"] && row["Recipient Name"])
      .map((row) => ({
        senderName: String(row["Sender Name"] ?? ""),
        senderPhone: String(row["Sender Phone (Do not add +1)"] ?? ""),
        senderAddress: String(row["Sender Address"] ?? ""),
        pickupNotes: String(row["Pickup Notes"] ?? ""),
        recipientName: String(row["Recipient Name"] ?? ""),
        recipientPhone: String(row["Recipient Phone (Do not add +1)"] ?? ""),
        recipientAddress: String(row["Recipient Address"] ?? ""),
        parcelsCount: Number(row["Parcels Count Number"]) || 1,
        deliveryNotes: String(row["Delivery Notes"] ?? ""),
      }));

    setParsedOrders(orders);
    setIsReviewing(true);
  };

  return (
    <div className="flex flex-col min-h-full">
      {!isReviewing ? (
        <div className="flex-1 p-6">
          <div className="flex flex-col items-center justify-center py-20 bg-background">
            <div className="w-full max-w-2xl mb-4 flex justify-center">
              <Button
                variant="link"
                className="gap-2 text-sm"
                onClick={() => {
                  const wb = utils.book_new();
                  const exampleRow = [
                    primaryAddress?.name ?? "John Smith", // Sender Name
                    primaryAddress?.phone ?? "4162223333", // Sender Phone
                    primaryAddress?.address
                      ? `${primaryAddress.address.line1}${primaryAddress.address.line2 ? `, ${primaryAddress.address.line2}` : ""}, ${primaryAddress.address.city}, ${primaryAddress.address.province} ${primaryAddress.address.postalCode}`
                      : "123 Main St, Toronto, ON M5V 2K4", // Sender Address
                    "Jane Doe", // Recipient Name
                    "6472223333", // Recipient Phone
                    "456 Oak Ave, Toronto, ON M4B 1A1", // Recipient Address
                    primaryAddress?.pickupNotes ?? "Ring doorbell twice", // Pickup Notes
                    "Leave at front desk", // Delivery Notes
                    "2", // Parcels Count
                  ];
                  const ws = utils.aoa_to_sheet([
                    TEMPLATE_HEADERS,
                    exampleRow,
                  ] as any[][]);
                  utils.book_append_sheet(wb, ws, "Template");
                  writeFileXLSX(wb, "example-bulk-upload.xlsx");
                }}
              >
                <Download className="h-4 w-4" />
                Download Example Spreadsheet
              </Button>
            </div>

            <div
              className="relative w-full max-w-2xl p-8 bg-card rounded-lg border border-border"
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-muted rounded-md">
                {files.length > 0 ? (
                  <div className="flex flex-col items-center justify-center">
                    <FileSpreadsheet className="w-12 h-12 text-primary" />
                    <p className="mt-4 text-muted-foreground">
                      {files[0].name}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center">
                    <FileSpreadsheet className="w-12 h-12 text-muted-foreground" />
                    <p className="mt-4 text-muted-foreground">
                      Drag and drop spreadsheet here or{" "}
                      <label
                        htmlFor="file-input"
                        className="font-medium text-primary cursor-pointer"
                      >
                        browse
                      </label>
                    </p>
                  </div>
                )}
                <input
                  id="file-input"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                  onChange={handleFileInput}
                />
              </div>
              {isInvalid && (
                <div className="mt-4 text-red-500">
                  Only spreadsheet files (CSV, XLS, XLSX) are allowed. Please
                  try again.
                </div>
              )}
            </div>
          </div>

          {files.length > 0 && (
            <div className="sticky bottom-0 left-0 right-0 p-4 z-50 bg-white">
              <div className="container max-w-7xl mx-auto flex justify-center">
                <Button
                  className="w-[300px] text-lg py-6 mb-6"
                  onClick={() => {
                    if (files[0]) {
                      void parseExcelFile(files[0]);
                    }
                  }}
                >
                  Review Orders
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-auto">
            <div className="mb-4 text-center">
              <h2 className="text-lg font-medium">
                Review Orders ({currentOrderIndex + 1} of {parsedOrders.length})
              </h2>
            </div>

            {parsedOrders.length > 0 && (
              <div className="w-full pb-8">
                <BulkCreateOrderReviewForm
                  key={currentOrderIndex}
                  order={parsedOrders[currentOrderIndex]}
                  isCompleted={completedOrders.has(currentOrderIndex)}
                  onSubmit={async (data: CreateOrderSchema) => {
                    try {
                      await createOrderMutation.mutateAsync(data);
                      setCompletedOrders((prev) =>
                        new Set(prev).add(currentOrderIndex)
                      );

                      if (currentOrderIndex < parsedOrders.length - 1) {
                        setCurrentOrderIndex(currentOrderIndex + 1);
                      } else {
                        setTimeout(() => {
                          setIsReviewing(false);
                          setParsedOrders([]);
                          setFiles([]);
                          setCompletedOrders(new Set());
                        }, 1500);
                      }
                    } catch (error) {
                      logger.error("Error creating order:", error);
                    }
                  }}
                  onFormReady={(submit) => {
                    currentFormSubmit.current = submit;
                  }}
                />
              </div>
            )}
          </div>

          {/* Sticky footer */}
          <div className="sticky bottom-0 left-0 right-0 bg-black backdrop-blur-3xl rounded-md">
            <div className="container p-8 flex justify-between items-center">
              <Button
                type="button"
                variant="outline"
                className="w-[150px] text-lg py-6 text-black bg-gray-200"
                onClick={() => {
                  setIsReviewing(false);
                  setParsedOrders([]);
                  setFiles([]);
                  setCompletedOrders(new Set());
                }}
              >
                Cancel Review
              </Button>
              <div className="flex items-center gap-4">
                <Button
                  type="submit"
                  className="w-[250px] text-lg py-6 bg-white text-black hover:bg-gray-100"
                  onClick={() => {
                    currentFormSubmit.current?.();
                  }}
                  disabled={
                    completedOrders.has(currentOrderIndex) ||
                    createOrderMutation.isPending
                  }
                >
                  {completedOrders.has(currentOrderIndex) ? (
                    <span className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-500" />
                      Order Created
                    </span>
                  ) : createOrderMutation.isPending ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Creating Order...
                    </span>
                  ) : (
                    `Create Order (${currentOrderIndex + 1}/${parsedOrders.length})`
                  )}
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentOrderIndex(currentOrderIndex - 1)}
                    disabled={currentOrderIndex === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentOrderIndex(currentOrderIndex + 1)}
                    disabled={currentOrderIndex === parsedOrders.length - 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
